import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useInterview } from '../../utils/interviewContext';
import { trackPostHogEvent } from '../../utils/posthogUtils';
import { EXTENDED_FLOW, getInterviewFlow } from '../../utils/interviewFlow';
import styles from './interviewQuestion.module.css';
import { Collapse } from '@mui/material';
import HelpRoundedIcon from '@mui/icons-material/HelpRounded';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import ProgressBar from '../.shared/ProgressBar/ProgressBar';
import Footer from '../.shared/footer/footer';
import Header from '../.shared/header/header';

/**
 * Entry gate for the extended flow's variant — the extended-flow counterpart
 * to Decision. Answering it is not one of the six questions in
 * EXTENDED_FLOW.steps: "guidance" hands off to that route unchanged at its
 * first step, and "I know what I want" leaves the funnel entirely, the same
 * way Decision's "I'm Ready" does for control.
 */
export default function InterviewGlp1Known() {
    const navigate = useNavigate();

    const { updateInterviewData, interviewData } = useInterview();
    const [ selectedChoice, setSelectedChoice ] = useState('none');

    // Read once per mount, so the chrome cannot disagree with itself if the
    // stored assignment changes while the page is open.
    const flow = useMemo(getInterviewFlow, []);

    // Populate from localStorage on component mount
    useEffect(() => {
        if (interviewData.glp1Known) {
            setSelectedChoice(interviewData.glp1Known);
        }

        // Track when user reaches the glp1-known gate
        trackPostHogEvent('interview_step_viewed', {
            interview_step: 'glp1-known',
            step_number: 0,
            step_type: 'entry_point',
            interview_flow: flow.id,
            has_existing_data: !!interviewData.glp1Known
        });
    }, [interviewData.glp1Known, flow]);

    const handleConfirm = (recentlyChosen) => {
        if (selectedChoice === 'none' && !recentlyChosen) return;

        const chosenChoice = recentlyChosen ? recentlyChosen : selectedChoice;

        setSelectedChoice(chosenChoice);

        // A visitor who already knows what they want skips qualification
        // entirely and goes straight to /products, same as Decision's "I'm
        // Ready" does for control. "guidance" hands off to the six-question
        // route exactly where it has always started.
        const nextDestination = chosenChoice === 'guidance'
            ? EXTENDED_FLOW.steps[0]
            : '/products';

        // Timeout to allow button selection animation to complete
        setTimeout(() => {
            // Save to interview context
            updateInterviewData('glp1Known', chosenChoice);

            // Track glp1-known selection
            trackPostHogEvent('interview_glp1_known_selected', {
                selected_choice: chosenChoice,
                interview_step: 'glp1-known',
                step_number: 0,
                interview_flow: flow.id,
                next_destination: nextDestination
            });

            navigate(nextDestination);
        }, 300);
    };

    const handleBack = () => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
        navigate('/');
    };

    return (
        <>
            <div className={styles.interviewPageContainer}>
                <Header hideNavigation={flow.hideNavigation} />
                <ProgressBar step={0} totalSteps={EXTENDED_FLOW.steps.length} />
                <Collapse
                    in
                    appear
                    timeout={500}
                    unmountOnExit
                    mountOnEnter
                    orientation="vertical"
                >
                    <div className={styles.questionContent}>
                        <h1 className={styles.title}>Do you already know which GLP-1 is right for you?</h1>
                        <div className={styles.buttonsContainer}>

                            <button
                                className={`${styles.interviewButton} ${selectedChoice === 'guidance' ? styles.selected : ''}`}
                                onClick={() => handleConfirm('guidance')}
                            >
                                <HelpRoundedIcon sx={{ color: `${selectedChoice === 'guidance' ? '#0D79FE' : '#4E4E4EFD'}` }} />
                                <span>I'd like guidance based on my health profile</span>
                            </button>

                            <button
                                className={`${styles.interviewButton} ${selectedChoice === 'know_what_i_want' ? styles.selected : ''}`}
                                onClick={() => handleConfirm('know_what_i_want')}
                            >
                                <CheckRoundedIcon sx={{ color: `${selectedChoice === 'know_what_i_want' ? '#0D79FE' : '#4E4E4EFD'}` }} />
                                <span>I've done my research — I know what I want</span>
                            </button>

                            <div className={styles.confirmationButtons}>
                                <button
                                    className={styles.secondaryButton}
                                    onClick={handleBack}
                                >
                                    Back
                                </button>
                                <button
                                    className={`${styles.primaryButton} ${selectedChoice === 'none' ? styles.disabledButton : ''}`}
                                    onClick={() => handleConfirm()}
                                >
                                    Continue
                                </button>
                            </div>

                        </div>
                    </div>
                </Collapse>
            </div>
            <Footer />
        </>
    );
}
